import { useTheme } from '@mui/material';
import { DataCard } from '@/reports/index';
import { styled, Pill, Stack, Text, Icons } from '@/theme/index';
import { commaFormatCurrency } from '@/utils/commaFormat/commaFormatCurrency';
import { nameize } from '@/utils/nameize';
import { useScreenSize } from '@/utils/useScreenSize';
import { FormattedDeed } from './Interfaces';
import { TimelineData } from './utils';

interface OwnershipTimelineCardProps {
  data: TimelineData;
  propertyAddress: string;
}

const Dot = styled(Stack)(({ theme }) => ({
  background: theme.palette.primary.main,
  borderRadius: '50%',
  position: 'absolute',
  left: '-23px',
  width: '15px',
  height: '15px',
}));

const DesktopYears = styled(Text)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileYears = styled(Text)(({ theme }) => ({
  display: 'block',
  fontSize: '16px',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

const YearsContainer = styled('div')(({ theme }) => ({
  textAlign: 'right',
  paddingRight: '15px',
  [theme.breakpoints.up('sm')]: {
    width: '200px',
  },
}));

export function OwnershipTimelineCard({ data }: OwnershipTimelineCardProps) {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  return (
    <DataCard>
      {data?.length &&
        data.map((timelineSection: FormattedDeed) => {
          return (
            <Stack
              direction="row"
              sx={{ marginBottom: '16px' }}
              key={timelineSection.id}
            >
              <YearsContainer>
                <DesktopYears variant="h6">
                  {timelineSection.startYear} - {timelineSection.endYear}
                </DesktopYears>
              </YearsContainer>
              <Stack
                sx={{
                  paddingLeft: '15px',
                  borderLeft: `1px solid ${theme.palette.primary.main}`,
                  width: '100%',
                }}
              >
                <Stack sx={{ position: 'relative' }}>
                  <Dot />
                </Stack>
                <MobileYears variant="h6">
                  {timelineSection.startYear} - {timelineSection.endYear}
                </MobileYears>
                {timelineSection.isCurrentOwner && (
                  <Pill
                    icon={<Icons.Check sx={{ fontSize: '1rem' }} />}
                    label="Current Owner"
                    color="secondary"
                    className="badge"
                    sx={{
                      marginBottom: (theme) => theme.spacing(1),
                      width: 'fit-content',
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                )}
                {timelineSection.name && (
                  <Text
                    variant={isMobile ? 'h4' : 'h2'}
                    sx={{
                      marginBottom: 1,
                      wordBreak: 'break-word',
                    }}
                  >
                    {nameize(timelineSection.name)}
                  </Text>
                )}
                {timelineSection.purchasePrice && (
                  <Stack direction="row">
                    <Text
                      variant="subtitle2"
                      sx={{
                        paddingRight: '5px',
                        fontWeight: 'bold',
                        minWidth: '135px',
                      }}
                    >
                      Purchased for:
                    </Text>
                    <Text variant="subtitle2">
                      {commaFormatCurrency(timelineSection.purchasePrice)}
                    </Text>
                  </Stack>
                )}
                {timelineSection.formattedPurchasedDate && (
                  <Stack direction="row">
                    <Text
                      variant="subtitle2"
                      sx={{
                        paddingRight: '5px',
                        fontWeight: 'bold',
                        minWidth: '135px',
                      }}
                    >
                      Date of purchase:
                    </Text>
                    <Text variant="subtitle2">
                      {timelineSection.formattedPurchasedDate}
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Stack>
          );
        })}
    </DataCard>
  );
}
